import { useState } from 'react';
import { useController } from '@rest-hooks/react';
import { useParams } from 'react-router-dom';

import ProductResource from 'resources/Profile/ProductResource';
import MembershipResource from 'resources/Profile/MembershipResource';
import TicketResource from 'resources/Profile/TicketResource';
import EventResource from 'resources/Profile/EventResource';

import { useError } from './useErrorController';
import { offerCategory } from './offersFormHelper';
import { checkLoginStatus } from './checkLoginStatus';

import { calculateLineMaxAmount } from './calculateProductMaxAmount';

import { t } from "i18n/index";

const defaultModalState = {
  authModal: false,
  redeemModal: false,
  checkoutModal: false,
  cartCheckoutModal: false,
};

const defaultCheckoutModalsState = {
  amountModal: false,
  paymentModal: false
};

export const usePreActionController = (organizationSlug, activeTag=null, eventSlug=null) => {
  
  const [currentAction, setCurrentAction] = useState(null);
  const [modals, setModals] = useState(defaultModalState)
  const [checkoutModals, setCheckoutModals] = useState(defaultCheckoutModalsState);

  const defaultCheckoutVars = {
    amount: currentAction?.product?.min_amount || 1,
    maxAmount: 1,
    step: 1,
    promoCode: ""
  };

  const [checkoutVars, setCheckoutVars] = useState(defaultCheckoutVars);
  
  const { fetch } = useController();
  const { handleError } = useError();
  const { ticketSlug } = useParams();

  // Tracks that the user initiated cart checkout but needs to authenticate
  // first. Once auth completes, handleSuccessfulAuth consumes this flag and
  // opens the cart checkout modal.
  const [cartCheckoutPending, setCartCheckoutPending] = useState(false);

  const handleAction = (product, redemptionMethod) => {
    setCurrentAction({ product, redemptionMethod });
    setCheckoutVars({ ...checkoutVars, amount: product?.min_amount || 1 });

    //if redemption, only redirect to redeemModal if logged in
    const isLoggedIn = checkLoginStatus();
    if (redemptionMethod === 'points') {
      const targetModal = isLoggedIn ? 'redeemModal' : 'authModal';
      setModals({ ...modals, [targetModal]: true });

    //if purchase, regardless of the login status, redirect to checkoutModal
    } else {
      setModals({ ...modals, checkoutModal: true });
    }
  };

  const handleCartCheckout = () => {
    const isLoggedIn = checkLoginStatus();
    if (!isLoggedIn) {
      setCartCheckoutPending(true);
      handleOpenAuthModal();
      return;
    }
    setModals({ ...defaultModalState, cartCheckoutModal: true });
  };

  const handleCartCheckoutClose = () => {
    setModals((prev) => ({ ...prev, cartCheckoutModal: false }));
  };

  const handleClose = () => {
    setCurrentAction(null);
    setModals(defaultModalState);
    setCheckoutModals(defaultCheckoutModalsState);
    setCheckoutVars(defaultCheckoutVars);
    setCartCheckoutPending(false);
  };

  const handleOpenAuthModal = () => {
    setCheckoutModals(defaultCheckoutModalsState);
    setModals({ ...modals, authModal: true });
  };

  const handleProductChange = (product) => {
    const newAction = { ...currentAction, product };
    setCurrentAction(newAction);
  };
  
  //handleSuccessfulAuth helper functions
  const fetchProducts = async () => {
    let products;

    const {category} = currentAction.product;
    if (category === offerCategory.ticket.value) {
      
      const event = await fetchEvent();

      if (event.single) {
        products = event.tickets;
      } else {
        // Return empty array if event ended during auth
        products = event.sessions?.length > 0 
          ? event.sessions.flatMap(session => session.tickets)
          : [];
      }
    } else if (eventSlug) {
      const event = await fetchEvent();
      products = event.event_products || [];
    } else {
      products = await fetch(ProductResource.list(), {organization_slug: organizationSlug, tag_id: activeTag?.id});
    }

    return products;
  };

  const fetchEvent = async () => {
    // the presence of ticketSlug determines if we are in a single event page with secret ticket or without
    // both TicketResource and EventResouce return an event
    // TicketResource returns an event with the secret ticket whereas EventResource returns an event with all tickets

    const event = ticketSlug
      ? await fetch(TicketResource.detail(), {organization_slug: organizationSlug, event_slug: eventSlug, id: ticketSlug })
      : await fetch(EventResource.detail(), {organization_slug: organizationSlug, id: eventSlug});
    
    return event;
  };

  const getUpdatedCheckoutVars = (newMaxAmount, amountNeedsUpdate, promoCodeNeedsUpdate) => {
    const {amount, promoCode} = checkoutVars;

    const newAmount = amountNeedsUpdate ? newMaxAmount : amount;
    const newPromoCode = promoCodeNeedsUpdate ? "" : promoCode;
    const newMessage = getUpdateMessage(newMaxAmount, amountNeedsUpdate, promoCodeNeedsUpdate);

    return {
      ...checkoutVars,
      amount: newAmount,
      maxAmount: newMaxAmount,
      promoCode: newPromoCode,
      message: newMessage
    };
  };

  const getUpdateMessage = (newMaxAmount, amountNeedsUpdate, promoCodeNeedsUpdate) => {
    if (amountNeedsUpdate && promoCodeNeedsUpdate) return `${t('errors:reachedMaxAmount', {maxAmount: newMaxAmount})} ${t('errors:entitledToCoupon')}`;
    if (amountNeedsUpdate) return t('errors:reachedMaxAmount', {maxAmount: newMaxAmount});
    if (promoCodeNeedsUpdate) return t('errors:entitledToCoupon');
    return '';
  };

  const updateCheckoutVars = (newMaxAmount, amountNeedsUpdate, promoCodeNeedsUpdate) => {
    const newCheckoutVars = getUpdatedCheckoutVars(newMaxAmount, amountNeedsUpdate, promoCodeNeedsUpdate);
    setCheckoutVars(newCheckoutVars);
  };

  const handleModalTransition = (amountNeedsUpdate, promoCodeNeedsUpdate) => {
    const newOpenModal = currentAction.redemptionMethod === 'points' ? 'redeemModal' : 'checkoutModal';
    const newModalState = { ...modals, [newOpenModal]: true, authModal: false};
    setModals(newModalState);

    if (newOpenModal === 'checkoutModal') {
      const openModalAmountModal = amountNeedsUpdate || promoCodeNeedsUpdate;
      const newCheckoutModalsState = { amountModal: openModalAmountModal, paymentModal: !openModalAmountModal };
      setCheckoutModals(newCheckoutModalsState);
    }
  };

  const handleSuccessfulAuth = async () => {
    await fetch(MembershipResource.list(), {organization_slug: organizationSlug});

    // Cart checkout auth recovery: continue the interrupted cart checkout by
    // opening the modal now that the user is authenticated.
    if (cartCheckoutPending) {
      setCartCheckoutPending(false);
      setModals({ ...defaultModalState, cartCheckoutModal: true });
      return;
    }

    const products = await fetchProducts();
    let product = products.find(product => {
      if (currentAction.product.event_product_listing_id) {
        return product.event_product_listing_id === currentAction.product.event_product_listing_id;
      }
      return product.id === currentAction.product.id;
    });
    if (!product) {
      handleClose();
      return;
    }

    // Re-attach the SKU picked before auth onto the fresh product (SELL06);
    // bail if it no longer exists or sold out during authentication.
    const selectedVariantId = currentAction.product.selected_variant?.id;
    if (selectedVariantId) {
      const freshVariant = (product.variants || []).find((variant) => variant.id === selectedVariantId);
      if (!freshVariant || !freshVariant.available) {
        handleError(t('errors:pickAnOption'));
        handleClose();
        return;
      }
      product = { ...product, selected_variant: freshVariant };
    } else if (product.variants?.length > 0) {
      handleError(t('errors:pickAnOption'));
      handleClose();
      return;
    }

    const {redeem_limit, redeemed_count, discount_price} = product;

    const alreadyRedeemedMax = redeem_limit && redeemed_count >= redeem_limit;
    if (alreadyRedeemedMax) {
      handleError(t('errors:redeemedMax'));
      handleClose();
      return;
    }

    if (discount_price || product.selected_variant) handleProductChange(product);

    const newMaxAmount = calculateLineMaxAmount(product, product.selected_variant);
    const amountNeedsUpdate = checkoutVars.amount > newMaxAmount;
    const promoCodeNeedsUpdate = !!discount_price;

    updateCheckoutVars(newMaxAmount, amountNeedsUpdate, promoCodeNeedsUpdate);
    handleModalTransition(amountNeedsUpdate, promoCodeNeedsUpdate);
  };

  return {
    checkoutModals,
    checkoutVars,
    currentAction,
    modals,
    onAction: handleAction,
    onCartCheckout: handleCartCheckout,
    onCartCheckoutClose: handleCartCheckoutClose,
    onAuthModalOpen: handleOpenAuthModal,
    onCheckoutModalsChange: setCheckoutModals,
    onCheckoutVarsChange: setCheckoutVars,
    onClose: handleClose,
    onProductChange: handleProductChange,
    onSuccessfulAuth: handleSuccessfulAuth,
  };
};
